import { MiraAvailabilityState, MiraPublishedMetric, MiraPublishedPeriod } from "@/data/models/Mira";
import {
  COLOR_MIRA_CONTROL_BOTH,
  COLOR_MIRA_CONTROL_BOTH_DARK,
  COLOR_MIRA_CONTROL_BOX,
  COLOR_MIRA_CONTROL_BOX_DARK,
  COLOR_MIRA_CONTROL_MINI,
  COLOR_MIRA_CONTROL_MINI_DARK,
  COLOR_MIRA_CONTROL_NONE,
  COLOR_MIRA_CONTROL_NONE_DARK,
} from "@/utils/colors";
import {
  MiraBar,
  MiraSegment,
  MiraTile,
  MiraViewDelta,
  deltaPercentText,
  formatPercent,
  medianMeasure,
  metricTitle,
  publishedMetric,
  spreadLine,
} from "@/utils/MiraDataQualityView";
import { MIRA_SORTED_BAR_ROWS, buildSortedBars } from "@/utils/MiraSortedBars";
import { unitText } from "@/utils/MiraCaseUnits";
import {
  MiraDryingContext,
  MiraFact,
  MiraFamilyKey,
  familyLabel,
  headlineFamily,
  neutralDelta,
} from "@/utils/MiraDryingView";

/**
 * The Geräte screen's view model: what each tile, fleet bar, category bar and fact shows, built from a
 * published period and its comparison period. Pure, like the Trocknung view model it builds on.
 *
 * Decided by Taimoor on 2026-10-01, each pinned by a test:
 * - **One Flottenauslastung tile per fleet** (Hans, Track #2610072 S7, 2026-10-23: "Box, Mini, Panel,
 *   all own devices"), each with the days in use and its devices, so a fleet is only ever compared with
 *   the same fleet. They replaced the single "größte Flotte" tile and the card that set the fleets side by
 *   side (Taimoor, 2026-10-23); a document from before IRES Cloud published them still shows the old tile.
 * - **Energy totals in MWh, per-project in kWh** (Hans, 2026-10-05): the total (DEV-004) and the
 *   energy card's "Total" read MWh, one decimal below 100 MWh and whole from 100; the per-project figure
 *   (DEV-005) and every other kWh value stay in kWh, the unit the meter readings use.
 * - As on Trocknung: changes are neutral, and only a published figure gets a tile.
 *
 * Sorted bars for the category mix, not a ring: clients have up to 14 categories (median 5, measured
 * 2026-10-01), and a ring is only readable up to about five parts.
 */

/** The fleets as the DEV-006 breakdown keys them. */
export const MIRA_FLEETS = ["own", "box", "mini", "panel"] as const;
export type MiraFleetKey = typeof MIRA_FLEETS[number];

/** One Flottenauslastung figure per fleet (#2610072 S7), in Hans's order: Box, Mini, Panel, own devices. */
export const MIRA_FLEET_METRICS: ReadonlyArray<{ fleet: MiraFleetKey; id: string }> = [
  { fleet: "box", id: "MIRA-DEV-011" },
  { fleet: "mini", id: "MIRA-DEV-012" },
  { fleet: "panel", id: "MIRA-DEV-013" },
  { fleet: "own", id: "MIRA-DEV-014" },
];

/** Whether the period's document carries the per-fleet figures (any state), so DEV-006 is not drawn. */
function hasFleetFigures(period: MiraPublishedPeriod | null): boolean {
  return MIRA_FLEET_METRICS.some(({ id }) => period?.metrics[id] !== undefined);
}

/** How many categories the mix shows before the rest are folded into one "Weitere" bar. */
export const MIRA_CATEGORY_ROWS = MIRA_SORTED_BAR_ROWS;

export function fleetLabel(fleet: MiraFleetKey, ctx: MiraDryingContext): string {
  return fleet === "own" ? ctx.t("mira.devices.fleets.own") : familyLabel(fleet as MiraFamilyKey, ctx);
}

/**
 * The fleet DEV-006 leads with: the HyDry category its caveat names, or the client's own devices, whose
 * caveat is the exit rule instead (`fleetExitAtLastUse`).
 */
export function headlineFleet(metric: MiraPublishedMetric | null): MiraFleetKey | null {
  if (metric?.caveat?.key === "fleetExitAtLastUse") return "own";
  return headlineFamily(metric);
}

export function formatKilowattHours(kwh: number, ctx: MiraDryingContext): string {
  return ctx.t("mira.devices.units.kWh", [ctx.formatNumber(Math.round(kwh))]);
}

/** Energy per drying day, one decimal: "12,3 kWh/Tag" (DEV-009, per project, not per device). */
export function formatKilowattHoursPerDay(kwh: number, ctx: MiraDryingContext): string {
  return ctx.t("mira.devices.units.kWhPerDay", [ctx.formatDecimal(kwh, 1)]);
}

/** Energy per square metre of drying area, one decimal: "14,7 kWh/m²" (DEV-010); a unit with a division takes no spaces (Hans, #2610112). */
export function formatKilowattHoursPerSquareMetre(kwh: number, ctx: MiraDryingContext): string {
  return ctx.t("mira.devices.units.kWhPerSquareMetre", [ctx.formatDecimal(kwh, 1)]);
}

/**
 * A power total in MWh (Hans, 2026-10-05): one decimal below 100 MWh ("87,5 MWh"), whole MWh from 100
 * ("875 MWh"), always through the locale-aware formatters. The input is in kWh, so the conversion
 * happens here and nothing is rounded before it.
 */
export function formatMegawattHours(kwh: number, ctx: MiraDryingContext): string {
  const mwh = kwh / 1000;
  const text = Math.abs(mwh) < 100 ? ctx.formatDecimal(mwh, 1) : ctx.formatNumber(Math.round(mwh));
  return ctx.t("mira.devices.units.MWh", [text]);
}

/**
 * A neutral change in MWh, formatted by the same rule as the total it sits under: one decimal below 100
 * MWh, whole from 100, the sign always written. The change is computed in kWh and converted, so no
 * precision is lost before the rounding.
 */
export function megawattDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
  ctx: MiraDryingContext
): MiraViewDelta | null {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }

  const mwh = (current - previous) / 1000;
  const whole = Math.abs(mwh) >= 100;
  const factor = whole ? 1 : 10;
  const change = Math.round(mwh * factor) / factor || 0;
  const direction = change > 0 ? "up" : change < 0 ? "down" : "flat";
  const sign = direction === "up" ? "+" : direction === "down" ? "−" : "±";
  const magnitude = whole ? ctx.formatNumber(Math.abs(change)) : ctx.formatDecimal(Math.abs(change), 1);
  const delta: MiraViewDelta = { text: ctx.t("mira.devices.units.MWh", [`${sign}${magnitude}`]), direction, tone: "neutral" };

  // The relative change beside it (#2610137).
  const percentText = direction !== "flat" ? deltaPercentText(current, previous, ctx) : undefined;
  return percentText ? { ...delta, percentText } : delta;
}

export function formatHours(hours: number, ctx: MiraDryingContext): string {
  return ctx.t("mira.devices.units.hours", [ctx.formatNumber(Math.round(hours))]);
}

function tile(
  id: string,
  icon: string,
  value: string,
  caption: string | null,
  delta: MiraViewDelta | null,
  ctx: MiraDryingContext,
  spread: string | null = null
): MiraTile {
  return { id, icon, title: metricTitle(id, ctx), value, caption, delta, status: null, statusLabel: "", spread };
}

/**
 * The HyDry Control line on the Geräte je Projekt tile (#2610137): Boxes and Minis per project over the
 * projects with one, and how many projects that is. `null` without the figure.
 */
function hydryControlLine(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): string | null {
  const metric = publishedMetric(period, "MIRA-DEV-015");
  return metric
    ? ctx.t("mira.devices.captions.hydryControlLine", [ctx.formatDecimal(metric.value ?? 0, 1), ctx.formatNumber(metric.basis ?? 0)])
    : null;
}

/**
 * The share bar on the Geräte je Projekt tile (#2610137): the finished projects with devices split into
 * Box only, Mini only, both and neither, from DEV-016. `null` for a document from before IRES Cloud split it.
 */
export function hydryControlParts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraSegment[] | null {
  const metric = publishedMetric(period, "MIRA-DEV-016");
  const breakdown = metric?.breakdown;
  const basis = metric?.basis ?? 0;
  if (!breakdown || breakdown.boxOnlyProjects === undefined || !(basis > 0)) {
    return null;
  }
  const dark = ctx.dark;
  const parts: Array<{ key: string; count: number; light: string; darkColor: string }> = [
    { key: "box", count: breakdown.boxOnlyProjects ?? 0, light: COLOR_MIRA_CONTROL_BOX, darkColor: COLOR_MIRA_CONTROL_BOX_DARK },
    { key: "mini", count: breakdown.miniOnlyProjects ?? 0, light: COLOR_MIRA_CONTROL_MINI, darkColor: COLOR_MIRA_CONTROL_MINI_DARK },
    { key: "both", count: breakdown.bothProjects ?? 0, light: COLOR_MIRA_CONTROL_BOTH, darkColor: COLOR_MIRA_CONTROL_BOTH_DARK },
    {
      key: "none",
      count: Math.max(0, basis - (breakdown.controlProjects ?? 0)),
      light: COLOR_MIRA_CONTROL_NONE,
      darkColor: COLOR_MIRA_CONTROL_NONE_DARK,
    },
  ];
  return parts.map((part) => ({
    key: part.key,
    label: ctx.t(`mira.devices.controlParts.${part.key}`),
    value: part.count,
    valueText: ctx.formatNumber(part.count),
    shareText: formatPercent(part.count / basis, ctx),
    color: dark ? part.darkColor : part.light,
  }));
}

/**
 * DEV-002 as published, value or not. `publishedMetric` treats a `null` value as nothing to show, which
 * is right for every other figure, but Betriebsstunden publishes `null` on purpose for a client with
 * hours derived from bookings only, and its breakdown still carries them.
 */
function operatingHours(period: MiraPublishedPeriod | null): MiraPublishedMetric | null {
  const metric = period?.metrics["MIRA-DEV-002"];
  return metric?.state === MiraAvailabilityState.AVAILABLE ? metric : null;
}

/**
 * The Geräte KPI row: a tile for each figure the period publishes, utilisation first, as fleet
 * dashboards lead with it.
 *
 * **Only a published figure gets a tile** (plan §5.7, as on Trocknung): one waiting on the company's
 * data is in the unlock list, one that cannot apply is not shown. The second lines are built here from
 * i18n, never from the document's German `presentation` string.
 */
export function buildDeviceTiles(
  period: MiraPublishedPeriod | null,
  comparison: MiraPublishedPeriod | null,
  ctx: MiraDryingContext
): MiraTile[] {
  const tiles: MiraTile[] = [];
  const points = (magnitude: string) => ctx.t("mira.format.deltaPoints", [magnitude]);
  const kwh = (magnitude: string) => ctx.t("mira.devices.units.kWh", [magnitude]);
  const kwhPerDay = (magnitude: string) => ctx.t("mira.devices.units.kWhPerDay", [magnitude]);
  const kwhPerSquareMetre = (magnitude: string) => ctx.t("mira.devices.units.kWhPerSquareMetre", [magnitude]);
  const hours = (magnitude: string) => ctx.t("mira.devices.units.hours", [magnitude]);
  const plain = (magnitude: string) => magnitude;

  // A published figure whose period had nothing (`value: null`) gets no tile; Betriebsstunden is built
  // from the published metric itself (see `operatingHours`), so its derived-hours case still shows.
  const add = (id: string, icon: string, build: (metric: MiraPublishedMetric, previous: MiraPublishedMetric | null) => MiraTile) => {
    if (period?.metrics[id]?.state !== MiraAvailabilityState.AVAILABLE) return;
    const metric = id === "MIRA-DEV-002" ? operatingHours(period) : publishedMetric(period, id);
    // No tile without a value (Taimoor, 2026-10-02): what a company would unlock goes on its own page (D2).
    if (metric) tiles.push(build(metric, id === "MIRA-DEV-002" ? operatingHours(comparison) : publishedMetric(comparison, id)));
  };

  if (hasFleetFigures(period)) {
    // One tile per fleet: the days in use over the fleet's days, and its devices as the case count.
    for (const { id } of MIRA_FLEET_METRICS) {
      add(id, "mdi-gauge", (metric, previous) =>
        tile(id, "mdi-gauge", formatPercent(metric.value ?? 0, ctx),
          `${ctx.t("mira.devices.facts.daysInUse", [
            ctx.formatNumber(metric.breakdown?.usedDays ?? 0),
            ctx.formatNumber(metric.breakdown?.fleetDays ?? 0),
          ])} · ${unitText("devices", metric.basis ?? 0, ctx)}`,
          neutralDelta((metric.value ?? 0) * 100, previous ? (previous.value ?? 0) * 100 : null, 0, points, ctx, false), ctx));
    }
  } else {
    // A document from before the per-fleet figures: the largest fleet's share, as it was drawn then.
    add("MIRA-DEV-006", "mdi-gauge", (metric, previous) => {
      const fleet = headlineFleet(metric);
      return tile("MIRA-DEV-006", "mdi-gauge", formatPercent(metric.value ?? 0, ctx),
        fleet ? ctx.t("mira.devices.captions.fleet", [fleetLabel(fleet, ctx)]) : null,
        neutralDelta((metric.value ?? 0) * 100, previous ? (previous.value ?? 0) * 100 : null, 0, points, ctx, false), ctx);
    });
  }

  add("MIRA-DEV-004", "mdi-flash", (metric, previous) => {
    const leftOut = metric.breakdown?.projectsLeftOut ?? 0;
    const basis = ctx.formatNumber(metric.basis ?? 0);
    // The total reads in MWh (Hans, 2026-10-05); the per-project figure (DEV-005) stays in kWh.
    return tile("MIRA-DEV-004", "mdi-flash", formatMegawattHours(metric.value ?? 0, ctx),
      leftOut > 0
        ? ctx.t("mira.devices.captions.energyLeftOut", [basis, ctx.formatNumber(leftOut)])
        : ctx.t("mira.devices.captions.energy", [basis]),
      megawattDelta(metric.value, previous?.value, ctx), ctx);
  });

  // DEV-005 and DEV-003 carry a median and percentiles (2026-10-06): the caption names the median and
  // the tile shows the P15 / P85 spread, as the duration tiles do; the mean stays the headline.
  add("MIRA-DEV-005", "mdi-flash-outline", (metric, previous) =>
    tile("MIRA-DEV-005", "mdi-flash-outline", formatKilowattHours(metric.value ?? 0, ctx),
      ctx.t("mira.devices.captions.energyPerProject", [
        medianMeasure(metric, (value) => formatKilowattHours(value, ctx), ctx),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      neutralDelta(metric.value, previous?.value, 0, kwh, ctx), ctx,
      spreadLine(metric.p15, metric.p85, (value) => formatKilowattHours(value, ctx), ctx)));

  // DEV-009: mean kWh per drying day per project (Hans's criterion: usage in the project, not per
  // device). One decimal, median caption and spread like DEV-005.
  add("MIRA-DEV-009", "mdi-lightning-bolt-outline", (metric, previous) =>
    tile("MIRA-DEV-009", "mdi-lightning-bolt-outline", formatKilowattHoursPerDay(metric.value ?? 0, ctx),
      ctx.t("mira.devices.captions.energyPerDryingDay", [
        medianMeasure(metric, (value) => formatKilowattHoursPerDay(value, ctx), ctx),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      neutralDelta(metric.value, previous?.value, 1, kwhPerDay, ctx), ctx,
      spreadLine(metric.p15, metric.p85, (value) => formatKilowattHoursPerDay(value, ctx), ctx)));

  // DEV-010 (Hans O9): total kWh over total m² of the finished projects with both, the per-project median
  // beside it. Projects where some device has no reading count with the energy they have, so they are
  // named in the caption: the figure reads lower than the truth there.
  add("MIRA-DEV-010", "mdi-texture-box", (metric, previous) => {
    const partial = metric.breakdown?.partialReadings ?? 0;
    const caption = ctx.t("mira.devices.captions.energyPerArea", [
      medianMeasure(metric, (value) => formatKilowattHoursPerSquareMetre(value, ctx), ctx),
      ctx.formatNumber(metric.basis ?? 0),
    ]);
    return tile("MIRA-DEV-010", "mdi-texture-box", formatKilowattHoursPerSquareMetre(metric.value ?? 0, ctx),
      partial > 0 ? `${caption} · ${ctx.t("mira.devices.captions.partialReadings", [ctx.formatNumber(partial)])}` : caption,
      neutralDelta(metric.value, previous?.value, 1, kwhPerSquareMetre, ctx), ctx,
      spreadLine(metric.p15, metric.p85, (value) => formatKilowattHoursPerSquareMetre(value, ctx), ctx));
  });

  add("MIRA-DEV-001", "mdi-calendar-range", (metric, previous) =>
    tile("MIRA-DEV-001", "mdi-calendar-range", ctx.formatNumber(Math.round(metric.value ?? 0)),
      ctx.t("mira.devices.captions.deviceDays", [ctx.formatNumber(metric.basis ?? 0)]),
      neutralDelta(metric.value, previous?.value, 0, plain, ctx), ctx));

  // Geräte je Projekt answers "which devices?" in one tile (Hans, #2610137): the drying devices (every
  // device but a Box or Mini) as the value, HyDry Control per project under it, and the share bar Box /
  // Mini / beide / keins at its foot. Its timeline and comparisons are the drying devices'.
  add("MIRA-DEV-003", "mdi-devices", (metric, previous) => ({
    ...tile("MIRA-DEV-003", "mdi-devices", ctx.formatDecimal(metric.value ?? 0, 1),
      ctx.t("mira.devices.captions.devicesPerProject", [
        medianMeasure(metric, (value) => ctx.formatDecimal(value, 1), ctx),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      neutralDelta(metric.value, previous?.value, 1, plain, ctx), ctx,
      spreadLine(metric.p15, metric.p85, (value) => ctx.formatDecimal(value, 1), ctx)),
    extraLine: hydryControlLine(period, ctx),
    parts: hydryControlParts(period, ctx),
  }));

  // HyDry Control (Hans, #2610112 X7): Boxes and Minis, never a Panel. Their own tiles only where Geräte je
  // Projekt has none (a client whose projects had only a Box or Mini), since that tile carries them.
  const inDevicesTile = tiles.some((entry) => entry.id === "MIRA-DEV-003");
  if (!inDevicesTile) {
    add("MIRA-DEV-015", "mdi-router-wireless", (metric, previous) =>
      tile("MIRA-DEV-015", "mdi-router-wireless", ctx.formatDecimal(metric.value ?? 0, 1),
        ctx.t("mira.devices.captions.controlDevicesPerProject", [
          medianMeasure(metric, (value) => ctx.formatDecimal(value, 1), ctx),
          ctx.formatNumber(metric.basis ?? 0),
        ]),
        neutralDelta(metric.value, previous?.value, 1, plain, ctx), ctx,
        spreadLine(metric.p15, metric.p85, (value) => ctx.formatDecimal(value, 1), ctx)));

    add("MIRA-DEV-016", "mdi-router-wireless-settings", (metric, previous) =>
      tile("MIRA-DEV-016", "mdi-router-wireless-settings", formatPercent(metric.value ?? 0, ctx),
        ctx.t("mira.devices.captions.controlProjectShare", [
          ctx.formatNumber(metric.breakdown?.controlProjects ?? 0),
          ctx.formatNumber(metric.basis ?? 0),
        ]),
        neutralDelta((metric.value ?? 0) * 100, previous ? (previous.value ?? 0) * 100 : null, 0, points, ctx, false), ctx));
  }

  // The device's own counter leads. A client with hours derived from bookings only has no headline
  // (`value: null`), and the tile says where its hours come from rather than showing a false zero.
  add("MIRA-DEV-002", "mdi-timer-outline", (metric, previous) =>
    metric.value === null || metric.value === undefined
      ? tile("MIRA-DEV-002", "mdi-timer-outline", "–",
          ctx.t("mira.devices.captions.operatingHoursDerivedOnly", [formatHours(metric.breakdown?.derivedHours ?? 0, ctx)]), null, ctx)
      : tile("MIRA-DEV-002", "mdi-timer-outline", formatHours(metric.value, ctx),
          ctx.t("mira.devices.captions.operatingHours", [ctx.formatNumber(metric.basis ?? 0)]),
          neutralDelta(metric.value, previous?.value, 0, hours, ctx), ctx));

  return tiles;
}

/**
 * The formatter the Verlauf card uses for a Geräte figure: utilisation as a share, energy in kWh, the
 * operating hours in hours, the rest as counts. Neutral, like every figure on the screen.
 */
export function devicesTrendFormatter(metricId: string, ctx: MiraDryingContext): (value: number) => string {
  switch (metricId) {
    case "MIRA-DEV-006":
    case "MIRA-DEV-011":
    case "MIRA-DEV-012":
    case "MIRA-DEV-013":
    case "MIRA-DEV-014":
    case "MIRA-DEV-016":
      return (value) => formatPercent(value, ctx);
    // The total is a bar series in MWh; the per-project figure stays in kWh.
    case "MIRA-DEV-004":
      return (value) => formatMegawattHours(value, ctx);
    case "MIRA-DEV-005":
      return (value) => formatKilowattHours(value, ctx);
    // DEV-009 is per drying day, one decimal, in kWh.
    case "MIRA-DEV-009":
      return (value) => formatKilowattHoursPerDay(value, ctx);
    case "MIRA-DEV-010":
      return (value) => formatKilowattHoursPerSquareMetre(value, ctx);
    case "MIRA-DEV-002":
      return (value) => formatHours(value, ctx);
    case "MIRA-DEV-003":
    case "MIRA-DEV-015":
      return (value) => ctx.formatDecimal(value, 1);
    default:
      return (value) => ctx.formatNumber(value);
  }
}

/**
 * Category id to the name a client sees. The caller passes Office's built-in categories first and the
 * client's settings in force after them, so a stored name wins and a built-in category the stored
 * settings do not carry still has its default name: a mix never shows an id. `leaveOut` is the Unknown
 * category, whose stored name is the English "Unknown", so the screen names it in the user's language.
 *
 * Takes plain lists rather than reading `DeviceSettingsManager`, which imports the Firestore handle from
 * `@/main` and would pull the whole app into this pure module and its tests.
 */
export function categoryNames(
  categories: ReadonlyArray<{ id?: string; categoryName?: string }>,
  leaveOut: string[] = []
): Record<string, string> {
  const names: Record<string, string> = {};

  for (const category of categories) {
    if (category.id && category.categoryName && !leaveOut.includes(category.id)) {
      names[category.id] = category.categoryName;
    }
  }

  return names;
}

/**
 * The members' category names by id for a group master, read one member at a time in the order given.
 *
 * On a master the category mix counts the members' devices, whose category ids the signed-in client's
 * own settings cannot name. This reads each member's categories and merges their names, so a mix shows
 * real names instead of folding every member id into "Unbekannte Kategorie".
 *
 * **First non-empty name wins**, so a category id two members name differently takes the first name
 * deterministically by member order. The built-in categories (`leaveOut`, e.g. the Unknown category
 * whose stored name is the English "Unknown") are skipped, so the screen names them itself. A member
 * whose read rejects is skipped, never fatal: its ids fold into the one unknown bar instead.
 *
 * Pure: the caller supplies the read, so the view model never imports the repositories or `@/main`.
 *
 * @param {string[]} memberIds - the members to read, in the order their names should win.
 * @param {Function} readCategories - a one-shot read of one client's device categories by id.
 * @param {string[]} leaveOut - category ids to skip (the built-in categories named by the screen).
 * @returns {Promise<Record<string, string>>} id to name for every member-defined category read.
 */
export async function collectMemberCategoryNames(
  memberIds: string[],
  readCategories: (clientId: string) => Promise<ReadonlyArray<{ id?: string; categoryName?: string }>>,
  leaveOut: string[] = []
): Promise<Record<string, string>> {
  const names: Record<string, string> = {};

  for (const memberId of memberIds) {
    try {
      const categories = await readCategories(memberId);
      categories.forEach((category) => {
        if (category.id && category.categoryName && !leaveOut.includes(category.id) && names[category.id] === undefined) {
          names[category.id] = category.categoryName;
        }
      });
    } catch (error) {
      // A member whose device settings cannot be read leaves its own category ids to fold into the one
      // "Unbekannte Kategorie" bar, never the card broken.
    }
  }

  return names;
}

/** DEV-008 as sorted bars: device days per category (see `buildSortedBars`). */
export function buildCategoryRows(
  period: MiraPublishedPeriod | null,
  names: Record<string, string>,
  ctx: MiraDryingContext
): MiraBar[] {
  return buildSortedBars(publishedMetric(period, "MIRA-DEV-008")?.breakdown, names, ctx.t("mira.devices.unknownCategory"), ctx, "share", "deviceDays");
}

/**
 * Stromverbrauch: the total, the mean per project, and how many projects each one leaves out, with the
 * reason. The total drops only consumption that is impossible even per device; the average also drops
 * projects over 120 kWh per day for the whole project (Hans, 2026-09-22), so one huge job cannot move it.
 */
export function energyFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const total = publishedMetric(period, "MIRA-DEV-004");
  const perProject = publishedMetric(period, "MIRA-DEV-005");
  const facts: MiraFact[] = [];

  if (total) {
    // The total in MWh (Hans, 2026-10-05), the per-project figure in kWh.
    facts.push({ label: ctx.t("mira.devices.facts.total"), value: formatMegawattHours(total.value ?? 0, ctx) });
  }
  if (perProject) {
    facts.push({ label: ctx.t("mira.devices.facts.perProject"), value: formatKilowattHours(perProject.value ?? 0, ctx) });
  }
  if (total) {
    facts.push({ label: ctx.t("mira.devices.facts.leftOutOfTotal"), value: ctx.formatNumber(total.breakdown?.projectsLeftOut ?? 0) });
  }
  if (perProject) {
    facts.push({ label: ctx.t("mira.devices.facts.leftOutOfAverage"), value: ctx.formatNumber(perProject.breakdown?.projectsLeftOut ?? 0) });
  }

  return facts;
}

/**
 * Betriebsstunden: the counter read off the device, and the hours derived from bookings beside it,
 * confirmed and unconfirmed. The two are never added: they count the same running in two ways.
 */
export function operatingHourFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const metric = operatingHours(period);
  if (!metric) return [];
  const breakdown = metric.breakdown ?? {};
  const implausible = (breakdown.implausibleCounterReadings ?? 0) + (breakdown.implausibleDerivedReadings ?? 0);

  return [
    ...(metric.value === null || metric.value === undefined
      ? []
      : [{ label: ctx.t("mira.devices.facts.counterHours"), value: formatHours(metric.value, ctx) }]),
    { label: ctx.t("mira.devices.facts.derivedConfirmed"), value: formatHours(breakdown.derivedConfirmedHours ?? 0, ctx) },
    { label: ctx.t("mira.devices.facts.derivedUnconfirmed"), value: formatHours(breakdown.derivedUnconfirmedHours ?? 0, ctx) },
    { label: ctx.t("mira.devices.facts.implausibleReadings"), value: ctx.formatNumber(implausible) },
  ];
}
